import {XIcon} from '@primer/octicons-react'
import {ActionList, Button, ButtonGroup, IconButton, type ButtonProps} from '@primer/react'
import type {Ref} from 'react'

import {mergeQueueButtonText} from '../../../helpers/merge-button-text'
import {MergeQueueMethod} from '../../../types'
import {ButtonWithDropdown} from '../common/ButtonWithDropdown'
import {BypassToggleMenuItem} from './merge-control/BypassToggle'
import {MergeDropdownOption} from './MergeDropdownOption'
import styles from './AnimatedMergeQueueButton.module.css'

type AnimatedMergeQueueButtonProps = {
  baseRefName: string
  buttonCount?: number
  buttonSize?: ButtonProps['size']
  confirmRef: Ref<HTMLButtonElement>
  inactive: boolean
  inactiveTooltipText: string
  isMergingMultiple: boolean
  isPending: boolean
  isPendingStackEnqueue: boolean
  isPrimary: boolean
  isStacked?: boolean
  selectedMethod: MergeQueueMethod
  shouldFocusPrimaryButton: boolean
  showBypassToggleOption: boolean
  showConfirmation: boolean
  viewerCanAddToMergeQueueSolo: boolean
  onCancel: () => void
  onConfirm: () => void
  onFocusPrimaryButton: () => void
  onPrimaryButtonClick: () => void
  onSelectGroup: () => void
  onSelectSolo: () => void
}

export function AnimatedMergeQueueButton({
  baseRefName,
  buttonCount,
  buttonSize,
  confirmRef,
  inactive,
  inactiveTooltipText,
  isMergingMultiple,
  isPending,
  isPendingStackEnqueue,
  isPrimary,
  isStacked,
  selectedMethod,
  shouldFocusPrimaryButton,
  showBypassToggleOption,
  showConfirmation,
  viewerCanAddToMergeQueueSolo,
  onCancel,
  onConfirm,
  onFocusPrimaryButton,
  onPrimaryButtonClick,
  onSelectGroup,
  onSelectSolo,
}: AnimatedMergeQueueButtonProps) {
  const confirmText = mergeQueueButtonText({
    mergeMethod: selectedMethod,
    confirming: true,
    inProgress: false,
    isMergingMultiple,
    isStacked,
  })

  // The cancel button takes the dropdown's place in the split button, so confirming does not move anything.
  const primaryAction = showConfirmation ? (
    <ButtonGroup className={styles.PrimaryGroup}>
      <Button
        className={styles.PrimaryConfirm}
        labelWrap
        size={buttonSize}
        ref={confirmRef}
        count={buttonCount}
        variant="primary"
        loading={isPending || isPendingStackEnqueue}
        loadingAnnouncement={mergeQueueButtonText({
          mergeMethod: selectedMethod,
          confirming: true,
          inProgress: true,
          isMergingMultiple,
          isStacked,
        })}
        onClick={onConfirm}
      >
        {confirmText}
      </Button>
      <IconButton
        icon={<XIcon className={styles.CancelIcon} />}
        aria-label="Cancel merge queue confirmation"
        variant="primary"
        size={buttonSize}
        disabled={isPending || isPendingStackEnqueue}
        onClick={onCancel}
      />
    </ButtonGroup>
  ) : (
    <ButtonWithDropdown
      className={styles.PrimaryGroup}
      labelWrap
      size={buttonSize}
      count={buttonCount}
      inactive={inactive}
      inactiveTooltipText={inactiveTooltipText}
      inactiveTooltipDirection="se"
      hideSecondaryButton={!viewerCanAddToMergeQueueSolo && !showBypassToggleOption}
      secondaryButtonAriaLabel="Select merge queue method"
      isPrimary={isPrimary}
      shouldFocusPrimaryButton={shouldFocusPrimaryButton}
      onFocusPrimaryButton={onFocusPrimaryButton}
      actionList={
        <ActionList selectionVariant="single">
          <ActionList.Group aria-label="Merge queue method">
            <MergeDropdownOption
              primaryText="Queue and merge in a group"
              secondaryText={`This pull request will be automatically grouped with other pull requests and merged into ${baseRefName}.`}
              selected={selectedMethod === MergeQueueMethod.GROUP}
              onSelect={onSelectGroup}
            />
            {viewerCanAddToMergeQueueSolo && (
              <MergeDropdownOption
                primaryText="Queue and force solo merge"
                secondaryText={`This pull request will be merged into ${baseRefName} by itself.`}
                selected={selectedMethod === MergeQueueMethod.SOLO}
                onSelect={onSelectSolo}
              />
            )}
          </ActionList.Group>
          {showBypassToggleOption && <BypassToggleMenuItem />}
        </ActionList>
      }
      onPrimaryButtonClick={onPrimaryButtonClick}
    >
      {mergeQueueButtonText({
        mergeMethod: selectedMethod,
        confirming: false,
        inProgress: isPending,
        isMergingMultiple,
        isStacked,
      })}
    </ButtonWithDropdown>
  )

  return (
    <div className={styles.AnimatedContainer}>
      <div className={styles.SizingRow} aria-hidden="true" inert>
        <Button className={styles.SizingButton} size={buttonSize} count={buttonCount} labelWrap disabled tabIndex={-1}>
          {confirmText}
        </Button>
        <span className={styles.TrailingSpacer} />
      </div>
      <div className={styles.ButtonRow}>
        <div className={styles.PrimaryButton}>{primaryAction}</div>
      </div>
    </div>
  )
}
